// ===== World 11: Jelly mountains – BOUNCES AND IMPACTS: MATERIALS AND WEIGHT =====
// Soft jelly bounces high, hard candy clacks (a small hop), marshmallow swallows the bounce.
// A heavy ball (jawbreaker) pushes a light ball far, but a light ball (candyfloss) barely moves a heavy one.
// The ball leaves a surface at the same angle as it hits it.
// Ball → goal: gumball → candy bowl (or the candy jar).
import { defineWorld, definePart } from '../../core/registry';
import type { Ctx2D, Gfx, GameBody, IntroHelpers, PhysicsApi, Reskin, CorePartType, SynthStyle, Vec, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { clamp, rr, outline, face } from '../../core/draw-helpers';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG } from './shared';

// ---------- constants: materials ----------
// e = how much of the speed into the surface comes back; tf = how much of the speed along the surface is kept
interface Mat { e: number; tf: number; min: number }
const MAT: Record<'jelly' | 'candy' | 'marshmallow', Mat> = {
  jelly: { e: 0.9, tf: 0.98, min: 2.5 },      // soft jelly: bounces almost as high as it fell from
  candy: { e: 0.45, tf: 0.95, min: 0 },     // hard candy: clack – a small hop
  marshmallow: { e: 0.02, tf: 0.25, min: 0 },       // marshmallow: swallows the bounce
};

/** Squash on the ball when it has just hit something (time, speed, contact normal) */
interface Squash { t: number; v: number; nx: number; ny: number }
declare module '../../core/types' { interface GameBody { jellySq?: Squash } }

// Shared bouncing surface: catches the speed just before the impact and mirrors it afterwards
// (the engine would otherwise let slow bounces die out)
function bouncer(api: PhysicsApi, b: GameBody, cx: number, cy: number, a: number, mat: Mat, onBounce?: (o: GameBody, v: number) => void) {
  const nx = -Math.sin(a), ny = Math.cos(a), todo: { o: GameBody; v: Vec; n: Vec; vn: number }[] = [];
  api.onHit(b, o => {
    if (!api.isDynamic(o)) return;
    const s = (o.position.x - cx) * nx + (o.position.y - cy) * ny >= 0 ? 1 : -1;
    const vn = o.velocity.x * nx * s + o.velocity.y * ny * s;
    if (vn < -0.8) todo.push({ o, v: { x: o.velocity.x, y: o.velocity.y }, n: { x: nx * s, y: ny * s }, vn });
  });
  return function after(api: PhysicsApi) {
    for (const q of todo.splice(0)) {
      const vt = { x: q.v.x - q.vn * q.n.x, y: q.v.y - q.vn * q.n.y };
      const out = Math.max(-q.vn * mat.e, mat.min && -q.vn > 3 ? mat.min : 0);
      api.Body.setVelocity(q.o, { x: vt.x * mat.tf + q.n.x * out, y: vt.y * mat.tf + q.n.y * out });
      if (onBounce) onBounce(q.o, -q.vn);
    }
  };
}

// ---------- drawing helpers ----------
// Balls: gumball (hero), jawbreaker (heavy), candyfloss (light)
function gumball(ctx: Ctx2D, r: number, c: [string, string, string]) {
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  gr.addColorStop(0, c[0]); gr.addColorStop(0.5, c[1]); gr.addColorStop(1, c[2]);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); outline(ctx, 2.5);
}
function jawbreaker(ctx: Ctx2D, r: number, roll: number | undefined) {
  ctx.save(); ctx.rotate(roll || 0);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = '#4B3C8C'; ctx.fill();
  ctx.save(); ctx.clip();
  const cs = ['#FF5A5F', '#FFC93C', '#37C16B', '#7FD3FF', '#FFFFFF'];
  for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.arc(0, 0, r * (1 - i * 0.2), 0, 7); ctx.fillStyle = cs[i]; ctx.fill(); }
  ctx.fillStyle = 'rgba(255,255,255,.55)';
  for (let i = 0; i < 6; i++) { const a = i * Math.PI / 3; ctx.beginPath(); ctx.arc(Math.cos(a) * r * .65, Math.sin(a) * r * .65, r * .08, 0, 7); ctx.fill(); }
  ctx.restore(); ctx.restore();
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); outline(ctx, 3);
}
function candyfloss(ctx: Ctx2D, r: number, t: number) {
  ctx.fillStyle = '#FFD6EC';
  for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4 + 0.3, w = Math.sin(t * 0.05 + i) * 1; ctx.beginPath(); ctx.arc(Math.cos(a) * r * .55, Math.sin(a) * r * .55, r * .5 + w, 0, 7); ctx.fill(); }
  ctx.beginPath(); ctx.arc(0, 0, r * .75, 0, 7); ctx.fill();
  ctx.strokeStyle = 'rgba(224,86,158,.55)'; ctx.lineWidth = 1.5;
  for (let i = 0; i < 4; i++) { ctx.beginPath(); ctx.arc(r * (i % 2 ? .2 : -.25), r * (i < 2 ? -.2 : .25), r * .25, 0, 4); ctx.stroke(); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.45)'; ctx.setLineDash([4, 5]); ctx.stroke(); ctx.setLineDash([]);
}
// Jelly block: see-through, wobbles when hit (squash and stretch)
function jelly(ctx: Ctx2D, w: number, h: number, wob: number, sel?: boolean, c?: [string, string]) {
  c = c || ['#8BF0B0', '#32B868'];
  const sx = 1 + wob * 0.12, sy = 1 - wob * 0.18;
  ctx.save(); ctx.scale(sx, sy);
  const g = ctx.createLinearGradient(0, -h / 2, 0, h / 2); g.addColorStop(0, c[0]); g.addColorStop(1, c[1]);
  // soft, rounded top
  ctx.beginPath(); ctx.moveTo(-w / 2, h / 2); ctx.lineTo(-w / 2, -h / 2 + 8); ctx.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + 10, -h / 2);
  ctx.lineTo(w / 2 - 10, -h / 2); ctx.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + 8); ctx.lineTo(w / 2, h / 2); ctx.closePath();
  ctx.globalAlpha = 0.92; ctx.fillStyle = g; ctx.fill(); ctx.globalAlpha = 1; outline(ctx, 3);
  ctx.fillStyle = 'rgba(255,255,255,.55)'; rr(ctx, -w / 2 + 8, -h / 2 + 4, w * 0.5, 5, 2.5); ctx.fill();
  ctx.beginPath(); ctx.arc(w / 2 - 14, -h / 2 + 7, 3, 0, 7); ctx.fill();
  // small bubbles in the jelly
  ctx.fillStyle = 'rgba(255,255,255,.4)';
  [[-w * .25, h * .15, 3], [w * .1, h * .2, 2.5], [w * .3, -h * .05, 2]].forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill(); });
  if (sel) { rr(ctx, -w / 2 - 6, -h / 2 - 6, w + 12, h + 12, 12); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
// Candy bar: hard, shiny, with twisted stripes
function candyBar(ctx: Ctx2D, w: number, h: number, sel?: boolean, flash?: boolean) {
  rr(ctx, -w / 2, -h / 2, w, h, h / 2); ctx.fillStyle = flash ? '#FFFFFF' : '#FFF4F8'; ctx.fill();
  ctx.save(); ctx.clip();
  ctx.fillStyle = '#FF5A7A';
  for (let x = -w / 2 - h; x < w / 2 + h; x += 18) { ctx.beginPath(); ctx.moveTo(x, h / 2); ctx.lineTo(x + 8, h / 2); ctx.lineTo(x + 8 + h, -h / 2); ctx.lineTo(x + h, -h / 2); ctx.closePath(); ctx.fill(); }
  ctx.restore();
  rr(ctx, -w / 2, -h / 2, w, h, h / 2); outline(ctx, 3);
  ctx.fillStyle = 'rgba(255,255,255,.75)'; rr(ctx, -w / 2 + 8, -h / 2 + 3, w - 16, 4, 2); ctx.fill();
  if (sel) { rr(ctx, -w / 2 - 6, -h / 2 - 6, w + 12, h + 12, h / 2 + 6); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}
// Marshmallow: soft white-pink cushion with dots
function marshmallow(ctx: Ctx2D, w: number, h: number, sq: number, sel?: boolean) {
  const s = 1 - sq * 0.25;
  ctx.save(); ctx.translate(0, h / 2); ctx.scale(1 + sq * 0.12, s); ctx.translate(0, -h / 2);
  rr(ctx, -w / 2, -h / 2, w, h, 14);
  const g = ctx.createLinearGradient(0, -h / 2, 0, h / 2); g.addColorStop(0, '#FFFFFF'); g.addColorStop(1, '#FFD3E6');
  ctx.fillStyle = g; ctx.fill(); outline(ctx, 3);
  ctx.fillStyle = 'rgba(255,150,190,.55)'; rr(ctx, -w / 2 + 4, h / 2 - 12, w - 8, 8, 4); ctx.fill();
  ctx.fillStyle = 'rgba(230,160,200,.6)';
  [[-w * .28, -2], [0, -6], [w * .26, 0], [-w * .1, 6]].forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 2.4, 0, 7); ctx.fill(); });
  if (sel) { rr(ctx, -w / 2 - 6, -h / 2 - 6, w + 12, h + 12, 16); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
// Liquorice stick (the plank)
function licorice(ctx: Ctx2D, w: number, h: number, sel: boolean, g: Gfx) {
  // block() only checks whether outline is set (it always draws the yellow ring), so sel is passed as a string
  g.block(ctx, 0, 0, w, h, 0, '#5A4A6E', '#2C2140', h / 2, { outline: sel ? '#FFC93C' : null });
  ctx.strokeStyle = 'rgba(255,255,255,.25)'; ctx.lineWidth = 2;
  for (let x = -w / 2 + 10; x < w / 2 - 6; x += 12) { ctx.beginPath(); ctx.moveTo(x, -h / 2 + 3); ctx.lineTo(x + 6, h / 2 - 3); ctx.stroke(); }
}
// Selection ring around a round part (selBox in draw-helpers is a rounded rectangle)
function selRing(ctx: Ctx2D, x: number, y: number, r: number) { ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }

// Intro animation, heavy against light: at the top the jawbreaker rolls into the ball (it flies far),
// at the bottom the candyfloss (the ball barely moves)
function introPush(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers, heavyFirst: boolean) {
  const tt = t % 240, hit = 70;
  const lanes: [number, boolean][] = [[100, true], [210, false]];
  for (const [y, heavy] of lanes) {
    ctx.fillStyle = '#E8B25C'; ctx.fillRect(20, y + 22, 420, 10);
    const px = tt < hit ? 40 + tt * 2.2 : 194 + (tt - hit) * (heavy ? 0.6 : -0.3);
    const bx = tt < hit ? 240 : 240 + (tt - hit) * (heavy ? 3.2 : 0.25);
    ctx.save(); ctx.translate(Math.max(30, px), y); if (heavy) jawbreaker(ctx, 22, tt * 0.05); else candyfloss(ctx, 22, t); ctx.restore();
    g.ball(ctx, h.fakeBall(Math.min(bx, 430), y + 6, tt > hit ? 2 : 0, 0, 16), 'hero', t);
  }
  // mark the lane that belongs to this part
  ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.setLineDash([6, 6]);
  rr(ctx, 12, (heavyFirst ? 100 : 210) - 34, 436, 72, 16); ctx.stroke(); ctx.setLineDash([]);
}

// ---------- skins ----------
const skins: WorldDef['skins'] = {
  // waffle biscuit
  jelly_biscuit(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const dw = b.dw!, dh = b.dh!;
    g.block(ctx, b.position.x, b.position.y, dw, dh, b.angle, '#FFE2A8', '#E8B25C', Math.min(8, dw / 2, dh / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(190,120,40,.45)'; ctx.lineWidth = 2;
    rr(ctx, -dw / 2 + 2, -dh / 2 + 2, dw - 4, dh - 4, 6); ctx.clip();
    for (let x = -dw / 2 - dh; x < dw / 2 + dh; x += 14) { ctx.beginPath(); ctx.moveTo(x, -dh / 2); ctx.lineTo(x + dh, dh / 2); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x + dh, -dh / 2); ctx.lineTo(x, dh / 2); ctx.stroke(); }
    ctx.restore();
  },
  // chocolate
  jelly_chocolate(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const dw = b.dw!, dh = b.dh!;
    g.block(ctx, b.position.x, b.position.y, dw, dh, b.angle, '#B07A55', '#6E4128', Math.min(8, dw / 2, dh / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(60,30,15,.4)'; ctx.lineWidth = 2;
    for (let x = -dw / 2 + 36; x < dw / 2 - 8; x += 36) { ctx.beginPath(); ctx.moveTo(x, -dh / 2 + 4); ctx.lineTo(x, dh / 2 - 4); ctx.stroke(); }
    for (let y = -dh / 2 + 36; y < dh / 2 - 8; y += 36) { ctx.beginPath(); ctx.moveTo(-dw / 2 + 4, y); ctx.lineTo(dw / 2 - 4, y); ctx.stroke(); }
    ctx.restore();
  },
};

// ---------- parts ----------
const parts = {
  // Jelly block: soft – bounces the ball almost as high as it fell from
  jelly_block: definePart({
    rects: [{ x: 0, y: 0, w: 120, h: 34 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG); jelly(ctx, 120, 34, 0, sel); ctx.restore(); },
    icon(ctx) { ctx.save(); ctx.scale(0.75, 0.75); jelly(ctx, 120, 34, 0); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 120, 34, { isStatic: true, angle: a, friction: 0.05, restitution: 0.3, chamfer: { radius: 8 }, label: 'jelly_block' }));
      b.kind = 'jelly_block';
      let hitT = -99, amp = 0;
      const after = bouncer(api, b, pl.x, pl.y, a, MAT.jelly, (o, v) => { hitT = api.S.t; amp = Math.min(1, v / 8); if (v > 2) api.sfx('jelly_boing', o.position.x, o.position.y, '#8BF0B0', 6); });
      return {
        layer: 'mid', after,
        draw(ctx, t, g, S) {
          const k = S.t - hitT, wob = k < 50 ? Math.sin(k * 0.45) * Math.exp(-k * 0.07) * amp : 0;
          ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); jelly(ctx, 120, 34, wob); ctx.restore();
        },
      };
    },
    intro(ctx, t, g, h) {
      // the ball bounces and bounces on the jelly – almost as high every time
      const per = 70, k = t % per, u = k / per, y = 220 - 34 / 2 - 16 - Math.sin(u * Math.PI) * 150;
      const wob = k < 12 ? Math.sin(k * 0.5) * (1 - k / 12) : 0;
      ctx.save(); ctx.translate(230, 220); jelly(ctx, 140, 34, wob); ctx.restore();
      g.ball(ctx, h.fakeBall(230, y, 0, u < 0.5 ? -3 : 3, 16), 'hero', t);
    },
  }),
  // Candy bar: hard – clack, a small hop
  jelly_candy: definePart({
    rects: [{ x: 0, y: 0, w: 130, h: 20 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG); candyBar(ctx, 130, 20, sel); ctx.restore(); },
    icon(ctx) { ctx.save(); ctx.rotate(-0.35); ctx.scale(0.75, 0.75); candyBar(ctx, 130, 20); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 130, 20, { isStatic: true, angle: a, friction: 0.02, restitution: 0.3, chamfer: { radius: 9 }, label: 'jelly_candy' }));
      b.kind = 'jelly_candy';
      let flash = -99;
      const after = bouncer(api, b, pl.x, pl.y, a, MAT.candy, (o, v) => { if (v > 1.5 && api.S.t - flash > 6) { flash = api.S.t; api.sfx('jelly_clack', o.position.x, o.position.y, '#FFFFFF', 4); } });
      return { layer: 'mid', after, draw(ctx, t, g, S) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); candyBar(ctx, 130, 20, false, S.t - flash < 5); ctx.restore(); } };
    },
    intro(ctx, t, g, h) {
      // jelly on the left (high bounce), candy on the right (small hop)
      ctx.save(); ctx.translate(130, 220); jelly(ctx, 120, 34, 0); ctx.restore();
      ctx.save(); ctx.translate(340, 222); candyBar(ctx, 130, 20); ctx.restore();
      const fall = (y0: number, floor: number, e: number, tt: number) => { let y = y0, v = 0; for (let i = 0; i < tt; i++) { v += 0.4; y += v; if (y > floor) { y = floor; v = -v * e; } } return y; };
      const tt = t % 200;
      g.ball(ctx, h.fakeBall(130, fall(40, 187, 0.9, tt), 0, 0, 16), 'hero', t);
      g.ball(ctx, h.fakeBall(340, fall(40, 196, 0.45, tt), 0, 0, 16), 'hero', t);
    },
  }),
  // Marshmallow: swallows the bounce – the ball stays put
  jelly_marshmallow: definePart({
    rects: [{ x: 0, y: 0, w: 70, h: 60 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG); marshmallow(ctx, 70, 60, 0, sel); ctx.restore(); },
    icon(ctx) { ctx.save(); ctx.scale(0.9, 0.9); marshmallow(ctx, 70, 60, 0); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 70, 60, { isStatic: true, angle: a, friction: 0.9, restitution: 0, chamfer: { radius: 14 }, label: 'jelly_marshmallow' }));
      b.kind = 'jelly_marshmallow';
      let hitT = -99;
      const after = bouncer(api, b, pl.x, pl.y, a, MAT.marshmallow, (o, v) => { hitT = api.S.t; if (v > 1.5) api.sfx('jelly_puff', o.position.x, o.position.y, '#FFE0EE', 6); });
      return {
        layer: 'mid', after,
        draw(ctx, t, g, S) { const k = S.t - hitT, sq = k < 24 ? Math.sin(k / 24 * Math.PI) * 0.8 : 0; ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); marshmallow(ctx, 70, 60, sq); ctx.restore(); },
      };
    },
    intro(ctx, t, g, h) {
      // the ball falls onto the marshmallow and stays there
      const tt = t % 200, y = Math.min(164, 30 + 0.012 * tt * tt * 2.2), sq = tt > 60 && tt < 85 ? Math.sin((tt - 60) / 25 * Math.PI) * 0.8 : 0;
      ctx.save(); ctx.translate(230, 210); marshmallow(ctx, 90, 60, sq); ctx.restore();
      g.ball(ctx, h.fakeBall(230, Math.min(y, 164 + sq * 6), 0, tt < 60 ? 4 : 0, 16), 'hero', t);
    },
  }),
  // Jawbreaker: a heavy ball the child places – pushes light things far
  jelly_heavy: definePart({
    rects: [{ x: 0, y: 0, w: 44, h: 44, circle: true }], circle: 22, rot: false,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); jawbreaker(ctx, 22, 0); ctx.restore(); if (sel) selRing(ctx, pl.x, pl.y, 30); },
    icon(ctx) { jawbreaker(ctx, 26, 0.3); },
    create(api, pl) {
      const b = api.add(api.Bodies.circle(pl.x, pl.y, 22, { density: 0.012, friction: 0.02, frictionStatic: 0.05, restitution: 0.5, frictionAir: 0.001, label: 'jelly_heavy' }));
      b.kind = 'jelly_heavy'; api.dynamic(b);
      api.onHit(b, o => { if (api.isBall(o) && b.speed > 1) api.sfx('jelly_bonk', b.position.x, b.position.y, '#FFC93C', 6); });
      return { layer: 'mid', draw(ctx) { ctx.save(); ctx.translate(b.position.x, b.position.y); jawbreaker(ctx, 22, b.angle); ctx.restore(); } };
    },
    intro(ctx, t, g, h) { introPush(ctx, t, g, h, true); },
  }),
  // Candyfloss ball: very light – barely moves heavy things
  jelly_light: definePart({
    rects: [{ x: 0, y: 0, w: 44, h: 44, circle: true }], circle: 22, rot: false,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); candyfloss(ctx, 22, 0); ctx.restore(); if (sel) selRing(ctx, pl.x, pl.y, 30); },
    icon(ctx) { candyfloss(ctx, 26, 0); },
    create(api, pl) {
      const b = api.add(api.Bodies.circle(pl.x, pl.y, 22, { density: 0.0004, friction: 0.02, frictionStatic: 0.05, restitution: 0.3, frictionAir: 0.004, label: 'jelly_light' }));
      b.kind = 'jelly_light'; api.dynamic(b);
      api.onHit(b, o => { if (api.isBall(o) && b.speed > 1) api.sfx('jelly_puff', b.position.x, b.position.y, '#FFD6EC', 4); });
      return { layer: 'mid', draw(ctx, t) { ctx.save(); ctx.translate(b.position.x, b.position.y); candyfloss(ctx, 22, t); ctx.restore(); } };
    },
    intro(ctx, t, g, h) { introPush(ctx, t, g, h, false); },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  jelly_boing: (tone) => { tone(260, 0.25, 'sine', 0.2, 520); tone(390, 0.2, 'sine', 0.1, 780, 0.05); },
  jelly_clack: (tone, noise) => { tone(2200, 0.05, 'square', 0.08); noise(0.05, 0.12, 4000, 3); },
  jelly_puff: (tone, noise) => { noise(0.25, 0.18, 500, 0.7); },
  jelly_bonk: (tone, noise) => { tone(180, 0.18, 'sine', 0.22, 90); noise(0.08, 0.15, 900, 2); },
};

// ---------- reskins (kit parts, balls and goals in candy style) ----------
// The plank and the long plank are liquorice sticks
const licoriceReskin: Reskin = (ctx, pl, sel, g, rects) => { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); licorice(ctx, r.w, r.h, sel, g); ctx.restore(); };
const reskin: Partial<Record<CorePartType, Reskin>> = {
  plank: licoriceReskin,
  long_plank: licoriceReskin,
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); licorice(ctx, 100, 16, false, g); ctx.restore(); },
};

type GoalPos = { x: number; y: number; w: number };
const goal = {
  // The candy bowl (bowl goal)
  bowl(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    // candy in the bowl
    const cs = ['#FF5A5F', '#FFC93C', '#37C16B', '#7FD3FF', '#C9B6F2', '#FF8FB8'];
    for (let i = 0; i < 9; i++) { ctx.beginPath(); ctx.arc(x - w * .4 + i * w * .1, y - 58 - (i % 3) * 5, 8, 0, 7); ctx.fillStyle = cs[i % 6]; ctx.fill(); outline(ctx, 2); }
    g.shadowOn(ctx, 12, 6);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 58); ctx.quadraticCurveTo(x - w / 2 + 2, y + 8, x, y + 8); ctx.quadraticCurveTo(x + w / 2 - 2, y + 8, x + w / 2 + 6, y - 58); ctx.closePath();
    ctx.fillStyle = 'rgba(200,236,255,.85)'; ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
    ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(x - w / 2 + 6, y - 46); ctx.quadraticCurveTo(x - w / 2 + 10, y - 10, x - w / 4, y); ctx.stroke();
    // foot
    rr(ctx, x - 26, y + 6, 52, 10, 4); ctx.fillStyle = '#C8ECFF'; ctx.fill(); outline(ctx, 2.5);
    ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 8, 0, 0, 7); ctx.strokeStyle = 'rgba(59,42,92,.8)'; ctx.lineWidth = 3; ctx.stroke();
    if (full) { ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 - 6, 5, 0, 0, 7); ctx.fillStyle = 'rgba(255,115,185,.6)'; ctx.fill(); }
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalPos) { const { x, y, w } = gl; ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 8, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.stroke(); ctx.restore(); },
  // The candy jar (bell goal): the lid pops off when the ball hits it
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
    const k = gl.hitT != null ? clamp((t - gl.hitT) / 25, 0, 1) : 0;
    ctx.save(); ctx.translate(gl.x, gl.y + Math.sin(t * 0.05) * 2);
    g.shadowOn(ctx, 10, 5);
    rr(ctx, -30, -28, 60, 62, 16); ctx.fillStyle = 'rgba(210,240,255,.85)'; ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
    const cs = ['#FF5A5F', '#FFC93C', '#37C16B', '#7FD3FF', '#FF8FB8'];
    for (let i = 0; i < 7; i++) { ctx.beginPath(); ctx.arc(-18 + (i % 4) * 12, 22 - Math.floor(i / 4) * 12, 6, 0, 7); ctx.fillStyle = cs[i % 5]; ctx.fill(); }
    ctx.fillStyle = 'rgba(255,255,255,.7)'; rr(ctx, -24, -20, 6, 36, 3); ctx.fill();
    // lid
    ctx.save(); ctx.translate(k * 18, -32 - k * 30); ctx.rotate(k * 0.6);
    rr(ctx, -34, -8, 68, 14, 6); ctx.fillStyle = '#FF73B9'; ctx.fill(); outline(ctx, 3);
    rr(ctx, -8, -16, 16, 10, 4); ctx.fillStyle = '#FF73B9'; ctx.fill(); outline(ctx, 2.5);
    ctx.restore();
    ctx.restore();
  },
};

// The balls: gumball (hero / mint helper), or a level's own skin (jelly_jaw = jawbreaker, jelly_cloud = candyfloss).
// Local drawBall: differs from drawBall() in draw-helpers (squash on impact, ball skins, sprinkles, no shadow).
function drawBall(ctx: Ctx2D, b: GameBody, kind: string, t: number) {
  const { x, y } = b.position, r = b.circleRadius!, sk = b.skin || (b.def && b.def.skin);
  ctx.save(); ctx.translate(x, y);
  // a little squash when the ball has just hit something
  const js = b.jellySq;
  const sq = js && t - js.t < 14 ? Math.sin((t - js.t) / 14 * Math.PI) * Math.min(0.25, js.v * 0.025) : 0;
  if (sq && js) { const a = Math.atan2(js.ny, js.nx); ctx.rotate(a); ctx.scale(1 - sq, 1 + sq * 0.6); ctx.rotate(-a); }
  if (sk === 'jelly_jaw') { jawbreaker(ctx, r, b.roll); ctx.save(); ctx.rotate(b.roll || 0); face(ctx, r * 0.75, b.velocity, b.sad); ctx.restore(); }
  else if (sk === 'jelly_cloud') { candyfloss(ctx, r, t); face(ctx, r * 0.7, b.velocity, b.sad); }
  else {
    gumball(ctx, r, kind === 'helper' ? ['#E8FFF6', '#7FE0CF', '#2FA593'] : ['#FFE0F0', '#FF73B9', '#C9307E']);
    ctx.save(); ctx.rotate(b.roll || 0);
    // small sugar sprinkles on the gumball
    const cs = ['#FFC93C', '#7FD3FF', '#FFFFFF'];
    [[-.5, .5], [.55, .45], [.1, -.7]].forEach(([px, py], i) => { ctx.save(); ctx.translate(px * r, py * r); ctx.rotate(i); ctx.fillStyle = cs[i]; rr(ctx, -3, -1.2, 6, 2.4, 1.2); ctx.fill(); ctx.restore(); });
    face(ctx, r, b.velocity, b.sad);
    ctx.restore();
  }
  ctx.beginPath(); ctx.ellipse(-r * .38, -r * .45, r * .26, r * .15, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill();
  ctx.restore();
}

// ---------- meta ----------
const synth: SynthStyle = { bpm: 78, root: 311.1, sc: 'major', prog: [0, 5, 1, 4], bar: 4, lead: 'bell', dens: 0.4, sev: true };
const meta: WorldMeta = {
  icon: '🍮', strings, music: 'jelly.mp3', synth,
  credits: { title: 'Cotton Candy', artist: 'MangMARU', id: 325902, url: 'https://pixabay.com/music/traditional-jazz-cotton-candy-325902/' },
};

export default defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(255,244,250,.55)',
  // squash on the ball when it hits something hard
  setup(api) {
    for (const b of api.S.dyn) if (api.isBall(b)) api.onHit(b, (o, p) => {
      const n = p.collision && p.collision.normal;
      if (n && b.speed > 2) b.jellySq = { t: api.S.t, v: b.speed, nx: n.x, ny: n.y };
    });
  },
  skins,
  ball: drawBall,
  goal,
  sounds,
  reskin,
  icons,
  parts,
  levels,
});
